/**
 * Document-level focus and caret restore for user-driven same-path Turbo updates.
 *
 * In scope: Drive morph/render (including the 422 validation re-render) and user-driven Frame
 * updates, for any visit whose destination pathname matches the current one — a changed query
 * string (table search, sort, paging, filter forms) still counts as in place.
 * Out of scope: genuine cross-page navigation (different pathname); unsolicited Turbo Stream patches.
 *
 * Capture/match/restore helpers: `focus-restore.ts`.
 * Rich-text deferred restore: `tryRestoreDeferredRichTextFocus` after TinyMCE boot.
 *
 * After a refused save the button that started the Turbo form submission keeps focus, or the
 * page's deliberate autofocus target when it has one (`focus-restore-submitter.ts`); the
 * refused-save reveal then moves focus to a Field error. The capture ends with the submission:
 * at the first render, or at `turbo:submit-end` when no render follows.
 *
 * Pairs with layout `turbo-refresh-scroll=preserve`; restore always uses `preventScroll`.
 */

import {
   captureFocusSnapshot,
   clearDeferredRichTextSnapshot,
   isDocumentRootFocus,
   restoreFocusSnapshot,
   tryRestoreDeferredRichTextFocus,
   type FocusSnapshot,
} from "./focus-restore";
import {
   capturePressedSubmitter,
   restorePressedSubmitter,
   type PressedSubmitter,
} from "./focus-restore-submitter";
import { isTextLikeInput } from "./focus-restore-selection";
import {
   hasEmptyBody,
   isFrameSubmission,
   recordFetchResponse,
   turboReadsBody,
   type SubmitDetail,
} from "./turbo-submit-event";

// Re-export the controller hook so consumers keep a single import path.
export { tryRestoreDeferredRichTextFocus };

const INVALID_MARKING_ATTRIBUTES = ["aria-invalid", "aria-describedby"];

let pendingSnapshot: FocusSnapshot | null = null;
let pressedSubmitter: PressedSubmitter | null = null;
let skipNextDriveRestore = false;
let streamRenderDepth = 0;

function captureIfAllowed(): void {
   if (skipNextDriveRestore || streamRenderDepth > 0) {
      return;
   }
   pendingSnapshot = captureFocusSnapshot();
}

function restorePending(root?: ParentNode): void {
   if (streamRenderDepth > 0) {
      return;
   }

   if (skipNextDriveRestore) {
      return;
   }

   const snapshot = pendingSnapshot;
   pendingSnapshot = null;
   restoreFocusSnapshot(snapshot, { root });
}

function onBeforeVisit(event: Event): void {
   const detail = (event as CustomEvent<{ url?: string }>).detail;
   if (!detail?.url) {
      return;
   }

   const destination = new URL(detail.url, window.location.href);
   const samePath = destination.pathname === window.location.pathname;

   if (!samePath) {
      skipNextDriveRestore = true;
      pendingSnapshot = null;
      pressedSubmitter = null;
      clearDeferredRichTextSnapshot();
   }
}

function onBeforeFetchResponse(event: Event): void {
   recordFetchResponse(event);
}

function onSubmitStart(event: Event): void {
   const submission = (event as CustomEvent<SubmitDetail>).detail?.formSubmission;
   // A submission inside a Turbo Frame is left to the frame logic.
   if (!submission || isFrameSubmission(submission)) {
      return;
   }
   pressedSubmitter = capturePressedSubmitter(submission, submission.submitter, submission.formElement);
}

function onSubmitEnd(event: Event): void {
   const { formSubmission, fetchResponse } = (event as CustomEvent<SubmitDetail>).detail ?? {};
   const pressed = pressedSubmitter;
   if (pressed?.submission !== formSubmission) {
      return;
   }
   // Only a refusal Turbo renders in place has something to restore onto: a success, a failed
   // request, a turbo-stream or taken-over response never renders, so the capture ends here.
   if (!fetchResponse || fetchResponse.succeeded || !turboReadsBody(fetchResponse)) {
      pressedSubmitter = null;
      return;
   }
   // Turbo does not render an empty or unreadable body either.
   void hasEmptyBody(fetchResponse).then((empty) => {
      if (empty && pressedSubmitter === pressed) {
         pressedSubmitter = null;
      }
   });
}

function onBeforeRender(): void {
   captureIfAllowed();
}

function onRender(): void {
   const pressed = pressedSubmitter;
   pressedSubmitter = null;
   if (skipNextDriveRestore) {
      skipNextDriveRestore = false;
      pendingSnapshot = null;
      clearDeferredRichTextSnapshot();
      return;
   }
   restorePending(document);
   restorePressedSubmitter(pressed);
}

/**
 * True when focus is absent (non-HTMLElement or document root) or lives inside `root`.
 * Used so a Turbo Frame update (e.g. assistant side panel) does not capture/restore a
 * control the person is using in another column.
 */
function focusIsInsideOrAbsent(root: EventTarget | null): boolean {
   if (!(root instanceof Element)) {
      return true;
   }
   const active = document.activeElement;
   if (!(active instanceof HTMLElement) || isDocumentRootFocus(active)) {
      return true;
   }
   return root.contains(active);
}

function onBeforeFrameRender(event: Event): void {
   // Frame-scoping: do not snapshot a control outside the frame being updated.
   if (!focusIsInsideOrAbsent(event.target)) {
      return;
   }
   captureIfAllowed();
}

function onFrameLoad(event: Event): void {
   // Leave pending Drive snapshots alone when this frame did not own focus.
   if (!focusIsInsideOrAbsent(event.target)) {
      return;
   }
   const frame = event.target instanceof Element ? event.target : document;
   restorePending(frame);
}

function onBeforeStreamRender(event: Event): void {
   // Unsolicited (and solicited) stream patches must not run this restore path.
   streamRenderDepth++;
   const detail = (event as CustomEvent<{ render?: (streamElement: Element) => void }>).detail;
   const originalRender = detail?.render;
   if (typeof originalRender !== "function") {
      // No wrap available — drop depth on next task so we do not stick permanently.
      queueMicrotask(() => {
         streamRenderDepth = Math.max(0, streamRenderDepth - 1);
      });
      return;
   }

   detail.render = (streamElement: Element) => {
      try {
         originalRender(streamElement);
      } finally {
         streamRenderDepth = Math.max(0, streamRenderDepth - 1);
      }
   };
}

/**
 * Morph path (decision 4): Turbo fires this cancelable event, on the element about to be
 * morphed, once per element in the old/new tree diff. When that element is the document's active
 * element and is text-like (input/textarea), cancel the morph for it — its value, caret and
 * attributes stay exactly as the person left them. The one exception is the pair that marks the
 * field invalid for a screen reader (`aria-invalid`, `aria-describedby`): a refused save renders
 * them on the input the person was typing in, so the input takes the incoming element's pair.
 *
 * The same holds for a `<dialog>` open as a modal (`showModal()`): the server page renders it
 * closed with its default text, so a morph would drop `open` and overwrite what it was opened
 * with. A closed or non-modal dialog still morphs.
 *
 * Everything else on the page still morphs.
 */
function onBeforeMorphElement(event: Event): void {
   const target = event.target;
   if (isOpenModalDialog(target)) {
      event.preventDefault();
      return;
   }
   if (!(target instanceof HTMLElement) || target !== document.activeElement) {
      return;
   }
   if (!isTextLikeInput(target)) {
      return;
   }
   event.preventDefault();

   const newElement = (event as CustomEvent<{ newElement?: unknown }>).detail?.newElement;
   if (newElement instanceof HTMLElement) {
      for (const name of INVALID_MARKING_ATTRIBUTES) {
         const incoming = newElement.getAttribute(name);
         if (incoming === null) {
            target.removeAttribute(name);
         } else {
            target.setAttribute(name, incoming);
         }
      }
   }
}

function isOpenModalDialog(target: EventTarget | null): boolean {
   return target instanceof HTMLDialogElement && target.hasAttribute("open") && target.matches(":modal");
}

/**
 * Installs the suite-wide Turbo lifecycle policy. Returns an uninstall function (tests).
 * Safe to call once from the shared `index.ts` entry.
 */
export function installFocusRestorePolicy(): () => void {
   document.addEventListener("turbo:before-visit", onBeforeVisit);
   document.addEventListener("turbo:submit-start", onSubmitStart);
   document.addEventListener("turbo:before-fetch-response", onBeforeFetchResponse);
   document.addEventListener("turbo:submit-end", onSubmitEnd);
   document.addEventListener("turbo:before-render", onBeforeRender);
   document.addEventListener("turbo:render", onRender);
   document.addEventListener("turbo:before-frame-render", onBeforeFrameRender);
   document.addEventListener("turbo:frame-load", onFrameLoad);
   document.addEventListener("turbo:before-stream-render", onBeforeStreamRender);
   document.addEventListener("turbo:before-morph-element", onBeforeMorphElement);

   return () => {
      document.removeEventListener("turbo:before-visit", onBeforeVisit);
      document.removeEventListener("turbo:submit-start", onSubmitStart);
      document.removeEventListener("turbo:before-fetch-response", onBeforeFetchResponse);
      document.removeEventListener("turbo:submit-end", onSubmitEnd);
      document.removeEventListener("turbo:before-render", onBeforeRender);
      document.removeEventListener("turbo:render", onRender);
      document.removeEventListener("turbo:before-frame-render", onBeforeFrameRender);
      document.removeEventListener("turbo:frame-load", onFrameLoad);
      document.removeEventListener("turbo:before-stream-render", onBeforeStreamRender);
      document.removeEventListener("turbo:before-morph-element", onBeforeMorphElement);
      pendingSnapshot = null;
      pressedSubmitter = null;
      clearDeferredRichTextSnapshot();
      skipNextDriveRestore = false;
      streamRenderDepth = 0;
   };
}
